// src/components/auth/LoginSuccessHandler.tsx
// Example usage, perhaps within your AuthProvider or a component handling login success

import React, { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUniqueUserId } from '../../contexts/UniqueUserIdContext';
import userService from '../../services/userService';
import logger from '../../utils/logger';

export const LoginSuccessHandler: React.FC = () => {
  const { user, isSignedIn } = useAuth(); // Get user details from AuthContext
  const uniqueUserId = useUniqueUserId(); // Get the device/browser ID

  useEffect(() => {

    // Check if user is authenticated, we have their details, AND we have the uniqueUserId
    if (isSignedIn && user && uniqueUserId) {
      // logger.debug(`User ${user.uid} logged in. Associating device ID: ${uniqueUserId}`);

      // Call the API to associate the device ID with the logged-in user
      userService.getCurrentUserToken()
      .then((userToken) => {
        // Optional but recommended: Check if userToken was successfully retrieved
        if (!userToken) {
          // If no token, we can't proceed. Throw an error to skip to the .catch block.
          logger.warn("Could not retrieve user token. Skipping device association.");
          throw new Error("User token is null or undefined.");
        }
        
        // logger.debug(`Retrieved user token. Associating device ID: ${uniqueUserId}`);
        // Call the second async function and RETURN its promise
        return userService.associateDeviceWithUser(userToken, uniqueUserId); // Pass auth token and device ID
      })
      .then(() => {
        // This block will execute *only after* associateDeviceWithUser successfully completes
        logger.info(`Successfully associated device ID ${uniqueUserId} with user ${user?.uid}`); // Assuming 'user' is accessible here
      })
      .catch((error: any) => {
        // This .catch() will handle errors from EITHER getCurrentUserToken OR associateDeviceWithUser
        logger.error(`Failed during token retrieval or device association for user ${user?.uid} and device ${uniqueUserId}:`, error);
        // Handle the error appropriately here (e.g., logging, maybe UI feedback)
      });
    } else {
      logger.debug( `In LoginSuccessHandler. for user [${user?.uid}] and unique ID: ${uniqueUserId}`);
    }
  }, [isSignedIn, user, uniqueUserId]); // Re-run effect if any of these change

  // This component might not render anything, just handle the side effect
  return null;
};